import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import imageCompression from 'browser-image-compression'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { KTIcon, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { fetchFormData, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { StepCommonProps } from '../../types/ads'

interface Props extends StepCommonProps {
  goToNext: () => void
}

const bannerSchema = Yup.object().shape({
  en_title: Yup.string().max(40, 'Max 40 Characters').required('English Title Is Required'),
  ru_title: Yup.string().max(40, 'Max 40 Characters').required('Russian Title Is Required'),
  uz_title: Yup.string().max(40, 'Max 40 Characters').required('Uzbek Title Is Required'),
  en_subtitle: Yup.string().max(60, 'Max 60 Characters'),
  ru_subtitle: Yup.string().max(60, 'Max 60 Characters'),
  uz_subtitle: Yup.string().max(60, 'Max 60 Characters'),
})

const ASPECT = 312 / 152
const LANGUAGES = [
  { key: 'en', label: 'English', flag: '/media/flags/united-states.svg' },
  { key: 'ru', label: 'Russian', flag: '/media/flags/russia.svg' },
  { key: 'uz', label: 'Uzbek', flag: '/media/flags/uzbekistan.svg' },
]

const BannerStep: FC<Props> = ({ t, adsId, adminId, vendorId, adData, imageBaseUrl, onSaveSuccess, goToNext }) => {
  const imgRef = useRef<HTMLImageElement | null>(null)

  const [bannerFile, setBannerFile] = useState<File | null>(null)
  const [bannerError, setBannerError] = useState('')

  // Cropper
  const [imgSrc, setImgSrc] = useState('')
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [showCropper, setShowCropper] = useState(false)
  const [cropError, setCropError] = useState('')
  const [saving, setSaving] = useState(false)

  const existingBanner = adData?.bannerImage ?? null

  const formik = useFormik({
    initialValues: {
      en_title: '',
      ru_title: '',
      uz_title: '',
      en_subtitle: '',
      ru_subtitle: '',
      uz_subtitle: '',
    },
    validationSchema: bannerSchema,
    onSubmit: async (values) => {
      // Validate banner file separately
      if (!bannerFile && !existingBanner) {
        setBannerError('Banner Image Is Required')
        return
      }
      setBannerError('')

      if (!adsId) { toasterrormsg('Please Save Placement First'); return }
      setSaving(true)
      try {
        const bannerFormData = new FormData()
        bannerFormData.append('adminId', String(adminId))
        bannerFormData.append('vendorId', String(vendorId))
        bannerFormData.append('adsId', String(adsId))
        bannerFormData.append('en_title', values.en_title)
        bannerFormData.append('ru_title', values.ru_title)
        bannerFormData.append('uz_title', values.uz_title)
        bannerFormData.append('en_subtitle', values.en_subtitle)
        bannerFormData.append('ru_subtitle', values.ru_subtitle)
        bannerFormData.append('uz_subtitle', values.uz_subtitle)
        if (adData?.adsbannerId) bannerFormData.append('adsbannerId', String(adData.adsbannerId))
        const completeSteps = { ...(adData?.completeSteps ?? {}), banner: true }
        bannerFormData.append('completeSteps', JSON.stringify(completeSteps))

        if (bannerFile) {
          bannerFormData.append('bannerImage', bannerFile)
          bannerFormData.append('image_312x152', bannerFile)
          bannerFormData.append('image_312x79', bannerFile)
        }

        const response = await fetchFormData('admin/ads/banner/save', bannerFormData)

        if (String(response?.status) === '200') {
          toastsuccessmsg(response?.message || 'Banner Saved')
          onSaveSuccess()
          goToNext()
        } else {
          toasterrormsg(response?.message || 'Failed To Save Banner')
        }
      } catch (error) {
        console.error('Banner save error:', error)
        toasterrormsg('Failed To Save Banner')
      } finally {
        setSaving(false)
      }
    },
  })

  // Pre-fill from adData
  useEffect(() => {
    if (!adData) return
    formik.setValues({
      en_title: adData?.en_title ?? '',
      ru_title: adData?.ru_title ?? '',
      uz_title: adData?.uz_title ?? '',
      en_subtitle: adData?.en_subtitle ?? '',
      ru_subtitle: adData?.ru_subtitle ?? '',
      uz_subtitle: adData?.uz_subtitle ?? '',
    })
  }, [adData])

  const previewUrl = useMemo(() => {
    if (bannerFile) return URL.createObjectURL(bannerFile)
    return existingBanner && imageBaseUrl ? imageBaseUrl + existingBanner : ''
  }, [bannerFile, existingBanner, imageBaseUrl])

  // File select
  const onSelectFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1 * 1024 * 1024) {
      setCropError('File Size Must Be Less Than 1 MB')
      if (e.target) e.target.value = ''
      return
    }
    setCropError('')
    const reader = new FileReader()
    reader.onload = () => { setImgSrc(reader.result as string); setShowCropper(true); setCrop(undefined) }
    reader.onerror = () => setCropError('Failed to read image file')
    reader.readAsDataURL(file)
    if (e.target) e.target.value = ''
  }, [])

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget
    const { width, height } = e.currentTarget
    if (!width || !height) return
    const cropWidth = Math.min(width, height * ASPECT)
    const cropHeight = cropWidth / ASPECT
    setCrop({ unit: 'px', x: (width - cropWidth) / 2, y: (height - cropHeight) / 2, width: cropWidth, height: cropHeight })
  }, [])

  const compressImage = useCallback(async (file: File): Promise<File> => {
    try {
      const c = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true })
      return new File([c], file.name, { type: c.type, lastModified: Date.now() })
    } catch { return file }
  }, [])

  const applyCrop = useCallback(async () => {
    if (!imgRef.current || !completedCrop) { setCropError('Please Select A Crop Area'); return }
    try {
      const canvas = document.createElement('canvas')
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height
      canvas.width = 312; canvas.height = 152
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(imgRef.current, completedCrop.x * scaleX, completedCrop.y * scaleY, completedCrop.width * scaleX, completedCrop.height * scaleY, 0, 0, 312, 152)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        const file = new File([blob], `banner_${Date.now()}.jpg`, { type: 'image/jpeg' })
        const compressed = await compressImage(file)
        setBannerFile(compressed)
        setShowCropper(false); setImgSrc(''); setCropError('')
      }, 'image/jpeg', 0.9)
    } catch { setCropError('An error occurred while cropping') }
  }, [completedCrop, compressImage])

  const closeCropper = useCallback(() => {
    setShowCropper(false)
    setImgSrc('')
  }, [])

  const titleFields = useMemo(() => [
    { key: 'en_title' as const, label: 'English', flag: LANGUAGES[0].flag },
    { key: 'ru_title' as const, label: 'Russian', flag: LANGUAGES[1].flag },
    { key: 'uz_title' as const, label: 'Uzbek', flag: LANGUAGES[2].flag },
  ], [])

  const subtitleFields = useMemo(() => [
    { key: 'en_subtitle' as const, label: 'English', flag: LANGUAGES[0].flag },
    { key: 'ru_subtitle' as const, label: 'Russian', flag: LANGUAGES[1].flag },
    { key: 'uz_subtitle' as const, label: 'Uzbek', flag: LANGUAGES[2].flag },
  ], [])

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Banner Content')}</h4>
        <p className='text-muted fs-7 mb-0'>{t('Upload your banner image and configure the preview')}</p>
      </div>

      {/* Image Upload + Preview */}
      <div className='row g-4 mb-6'>
        <div className='col-12 col-md-6'>
          <label className='adform-label'>{t('Banner Image')} <span className='text-danger'>*</span></label>
          <input type='file' accept='image/jpeg,image/png,image/webp' className='form-control form-control-lg' onChange={onSelectFile} />
          <div className='text-muted fs-8 mt-1'>JPG, PNG, WEBP · max 1 MB · ratio 3:1 (312×152px)</div>
          {bannerError && <div className='text-danger fs-7 mt-1'>{bannerError}</div>}
          {cropError && !showCropper && <div className='text-danger fs-7 mt-1'>{cropError}</div>}
        </div>
        <div className='col-12 col-md-6'>
          <label className='adform-label'>{t('Banner Preview')} (3:1 · 312×152px)</label>
          <div className='adform-banner-preview'>
            {previewUrl ? (
              <img src={previewUrl} alt='Banner' onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png') }} />
            ) : (
              <div className='adform-banner-preview__empty'>
                <KTIcon iconName='picture' className='fs-3x text-muted' />
                <span className='text-muted fs-7 mt-2'>{t('Upload an image to see preview')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cropper */}
      {showCropper && imgSrc && (
        <div className='adform-crop-overlay'>
          <div className='adform-crop-modal'>
            <div className='adform-crop-modal__header'>
              <h5 className='fw-bold mb-0'>{t('Crop Banner Image')}</h5>
              <button type='button' className='btn btn-sm btn-icon btn-light' onClick={closeCropper}>
                <KTIcon iconName='cross' className='fs-2' />
              </button>
            </div>
            <div className='adform-crop-modal__body'>
              <ReactCrop crop={crop} onChange={setCrop} onComplete={setCompletedCrop} aspect={ASPECT}>
                <img src={imgSrc} onLoad={onImageLoad} alt='Crop' className='adform-crop-img' />
              </ReactCrop>
              {cropError && <div className='text-danger fs-7 mt-2'>{cropError}</div>}
            </div>
            <div className='adform-crop-modal__footer'>
              <button type='button' className='btn btn-light btn-sm px-4' onClick={closeCropper}>{t('Cancel')}</button>
              <button type='button' className='btn btn-primary btn-sm px-4' onClick={applyCrop}>{t('Apply Crop')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Banner Title */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <KTIcon iconName='text' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Banner Title')}</h5>
        </div>
        <div className='row g-4'>
          {titleFields.map((f) => (
            <div className='col-12 col-md-4' key={f.key}>
              <div className='adform-lang-card'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <img src={toAbsoluteUrl(f.flag)} alt={f.label} className='adform-lang-flag' />
                  <span className='fw-bold fs-7'>{f.label}</span>
                </div>
                <label className='adform-label'>{t('Title')} <span className='text-danger'>*</span></label>
                <input
                  type='text'
                  className={`form-control form-control-lg ${formik.touched[f.key] && formik.errors[f.key] ? 'is-invalid' : ''}`}
                  placeholder={`Enter title in ${f.label}`}
                  maxLength={40}
                  name={f.key}
                  value={formik.values[f.key]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className='d-flex justify-content-between mt-1'>
                  {formik.touched[f.key] && formik.errors[f.key] ? (
                    <span className='text-danger fs-8'>{formik.errors[f.key]}</span>
                  ) : <span />}
                  <span className='text-muted fs-9'>{(formik.values[f.key] || '').length}/40</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Banner Subtitle */}
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <KTIcon iconName='text-align-left' className='fs-2 text-primary' />
          <h5 className='fw-bold mb-0'>{t('Banner Subtitle')}</h5>
          <span className='badge badge-light-warning fs-9'>{t('Optional')}</span>
        </div>
        <div className='row g-4'>
          {subtitleFields.map((f) => (
            <div className='col-12 col-md-4' key={f.key}>
              <div className='adform-lang-card'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <img src={toAbsoluteUrl(f.flag)} alt={f.label} className='adform-lang-flag' />
                  <span className='fw-bold fs-7'>{f.label}</span>
                </div>
                <label className='adform-label'>{t('Subtitle')} ({t('Optional')})</label>
                <input
                  type='text'
                  className='form-control form-control-lg'
                  placeholder={`Enter subtitle in ${f.label}`}
                  maxLength={60}
                  name={f.key}
                  value={formik.values[f.key]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className='text-end mt-1'><span className='text-muted fs-9'>{(formik.values[f.key] || '').length}/60</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className='d-flex justify-content-end mt-6'>
        <button type='button' className='btn btn-primary d-flex align-items-center gap-2 px-5 py-3' onClick={() => formik.handleSubmit()} disabled={saving}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : <KTIcon iconName='check' className='fs-3' />}
          <span>{t('Save & Next')}</span>
          <KTIcon iconName='arrow-right' className='fs-3' />
        </button>
      </div>
    </>
  )
}

export { BannerStep }
